import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getOrder, updateOrder, uploadOrderFile, listOrderFiles, deleteOrderFile, createOrderUploadToken, listOrderUploadTokens, revokeOrderUploadToken, getFileUrl } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft, Package, User, Factory, Car, Truck, MapPin, Clock,
  FileText, Upload, Trash, CheckCircle, Warning, LinkSimple, Copy, EnvelopeSimple, Prohibit,
  Eye, Download
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const ORDER_STATUSES = [
  'new_lead', 'waiting_documents', 'documents_received', 'car_sourcing',
  'vehicle_confirmed', 'payment_pending', 'payment_received', 'shipping_booked',
  'in_transit', 'container_loaded', 'bl_issued', 'arrived_algeria',
  'customs_clearance', 'ready_delivery', 'delivered', 'cancelled'
];

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [order, setOrder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editData, setEditData] = useState({});
  // Sharable client-upload links
  const [tokens, setTokens] = useState([]);
  const [creatingToken, setCreatingToken] = useState(false);
  const [tokenForm, setTokenForm] = useState({
    expires_in_days: 14,
    max_uploads: 20,
    send_email: false,
    send_to_email: '',
    note_to_client: '',
  });

  const loadOrder = useCallback(async () => {
    try {
      const res = await getOrder(id);
      setOrder(res.data);
      setEditData({
        status: res.data.status,
        container_number: res.data.container_number || '',
        bl_number: res.data.bl_number || '',
        tracking_reference: res.data.tracking_reference || '',
        estimated_arrival: res.data.estimated_arrival || '',
        current_location: res.data.current_location || '',
        departure_date: res.data.departure_date || '',
        notes: res.data.notes || ''
      });
    } catch {
      toast.error('Failed to load order');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadDocuments = useCallback(async () => {
    try {
      const [filesRes, tokensRes] = await Promise.all([
        listOrderFiles(id),
        listOrderUploadTokens(id),
      ]);
      setDocuments(filesRes.data || []);
      setTokens(tokensRes.data || []);
    } catch {
      // silent — view still works
    }
  }, [id]);

  useEffect(() => { loadOrder(); loadDocuments(); }, [loadOrder, loadDocuments]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateOrder(id, editData);
      toast.success('Order updated');
      loadOrder();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        await uploadOrderFile(id, fd);
        ok += 1;
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || 'Upload failed'}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (ok) toast.success(`${ok} file${ok > 1 ? 's' : ''} uploaded`);
    loadDocuments();
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteOrderFile(id, fileId);
      toast.success('File deleted');
      loadDocuments();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const writeToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {}
    // Fallback for Safari/iOS or non-secure contexts
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleCreateToken = async () => {
    setCreatingToken(true);
    try {
      const payload = {
        expires_in_days: Number(tokenForm.expires_in_days) || 14,
        max_uploads: Number(tokenForm.max_uploads) || 20,
        send_email: !!tokenForm.send_email,
        send_to_email: tokenForm.send_to_email.trim() || undefined,
        note_to_client: tokenForm.note_to_client.trim() || undefined,
      };
      const res = await createOrderUploadToken(id, payload);
      const url = res.data.url || `${window.location.origin}/u/${res.data.token}`;
      const copied = await writeToClipboard(url);
      toast.success(copied ? 'Upload link created and copied to clipboard' : 'Upload link created');
      if (res.data.emailed_to) {
        toast.success(`Sent to ${res.data.emailed_to}`);
      }
      setTokenForm({ ...tokenForm, send_to_email: '', note_to_client: '' });
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create link');
    } finally {
      setCreatingToken(false);
    }
  };

  const copyTokenUrl = async (tok) => {
    const url = `${window.location.origin}/u/${tok.token}`;
    const ok = await writeToClipboard(url);
    if (ok) toast.success('Link copied');
    else toast.error('Copy failed — long-press to copy manually');
  };

  const handleRevokeToken = async (tok) => {
    if (!window.confirm('Revoke this upload link? The client will no longer be able to use it.')) return;
    try {
      await revokeOrderUploadToken(id, tok.id);
      toast.success('Link revoked');
      loadDocuments();
    } catch {
      toast.error('Failed to revoke');
    }
  };

  const tokenStatus = (tok) => {
    if (tok.is_revoked) return { label: 'Revoked', color: 'text-red-600 bg-red-50' };
    try {
      if (new Date(tok.expires_at) < new Date()) return { label: 'Expired', color: 'text-amber-700 bg-amber-50' };
    } catch {}
    if ((tok.uploads_count || 0) >= (tok.max_uploads || 20)) return { label: 'Full', color: 'text-amber-700 bg-amber-50' };
    return { label: 'Active', color: 'text-green-700 bg-green-50' };
  };

  const statusLabels = ORDER_STATUSES.reduce((acc, s) => {
    acc[s] = t(`status.${s}`);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div data-testid="order-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/orders')} data-testid="back-to-orders">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Package weight="duotone" className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight font-mono">{order.order_number}</h1>
          </div>
          <p className="text-muted-foreground text-sm">Order details and tracking</p>
        </div>
        <span className={`status-badge status-${order.status}`} data-testid="order-status-badge">
          {statusLabels[order.status]}
        </span>
      </div>

      <Tabs defaultValue={typeof window !== 'undefined' && window.location.hash === '#documents' ? 'documents' : 'details'} className="space-y-6">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="tracking">Tracking</TabsTrigger>
          <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Client */}
            <div className="card-brutalist">
              <div className="flex items-center gap-2 mb-3">
                <User weight="duotone" className="w-5 h-5 text-muted-foreground" />
                <span className="overline">{t('orders.client')}</span>
              </div>
              {order.client ? (
                <div>
                  <p className="font-bold">{order.client.name}</p>
                  <p className="text-sm text-muted-foreground">{order.client.email}</p>
                  <p className="text-sm text-muted-foreground">{order.client.phone}</p>
                </div>
              ) : (
                <p className="text-muted-foreground">Not assigned</p>
              )}
            </div>

            {/* Supplier */}
            <div className="card-brutalist">
              <div className="flex items-center gap-2 mb-3">
                <Factory weight="duotone" className="w-5 h-5 text-muted-foreground" />
                <span className="overline">{t('orders.supplier')}</span>
              </div>
              {order.supplier ? (
                <div>
                  <p className="font-bold">{order.supplier.name}</p>
                  <p className="text-sm text-muted-foreground">{order.supplier.contact_email}</p>
                </div>
              ) : (
                <p className="text-muted-foreground">Not assigned</p>
              )}
            </div>

            {/* Vehicle */}
            <div className="card-brutalist">
              <div className="flex items-center gap-2 mb-3">
                <Car weight="duotone" className="w-5 h-5 text-muted-foreground" />
                <span className="overline">{t('orders.vehicle')}</span>
              </div>
              {order.vehicle ? (
                <div>
                  <p className="font-bold">{order.vehicle.brand_name} {order.vehicle.model_name}</p>
                  <p className="text-sm text-muted-foreground">{order.vehicle.year} • {order.vehicle.color}</p>
                  {order.vehicle.purchase_price && (
                    <p className="text-primary font-bold">${order.vehicle.purchase_price.toLocaleString()}</p>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">Not assigned</p>
              )}
            </div>

            {/* Shipping Company */}
            <div className="card-brutalist">
              <div className="flex items-center gap-2 mb-3">
                <Truck weight="duotone" className="w-5 h-5 text-muted-foreground" />
                <span className="overline">{t('orders.shippingCompany')}</span>
              </div>
              {order.shipping_company ? (
                <div>
                  <p className="font-bold">{order.shipping_company.name}</p>
                  <p className="text-sm text-muted-foreground">{order.shipping_company.shipping_route}</p>
                </div>
              ) : (
                <p className="text-muted-foreground">Not assigned</p>
              )}
            </div>
          </div>

          {/* Edit Section */}
          <div className="card-brutalist">
            <h3 className="font-bold mb-4">Update Order</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('orders.status')}</Label>
                <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                  <SelectTrigger className="input-brutalist" data-testid="order-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORDER_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{statusLabels[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('orders.containerNumber')}</Label>
                <Input
                  value={editData.container_number}
                  onChange={(e) => setEditData({ ...editData, container_number: e.target.value })}
                  className="input-brutalist font-mono"
                  data-testid="order-container-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('orders.blNumber')}</Label>
                <Input
                  value={editData.bl_number}
                  onChange={(e) => setEditData({ ...editData, bl_number: e.target.value })}
                  className="input-brutalist font-mono"
                  data-testid="order-bl-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('orders.estimatedArrival')}</Label>
                <Input
                  type="date"
                  value={editData.estimated_arrival}
                  onChange={(e) => setEditData({ ...editData, estimated_arrival: e.target.value })}
                  className="input-brutalist"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('orders.departureDate')}</Label>
                <Input
                  type="date"
                  value={editData.departure_date}
                  onChange={(e) => setEditData({ ...editData, departure_date: e.target.value })}
                  className="input-brutalist"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('orders.currentLocation')}</Label>
                <Input
                  value={editData.current_location}
                  onChange={(e) => setEditData({ ...editData, current_location: e.target.value })}
                  placeholder="e.g., At sea, Suez Canal"
                  className="input-brutalist"
                />
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editData.notes}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                rows={2}
                className="input-brutalist"
              />
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="order-save-btn">
                {saving ? 'Saving...' : t('common.save')}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Tracking Tab */}
        <TabsContent value="tracking">
          <div className="card-brutalist">
            <h3 className="font-bold mb-4">Shipment Tracking</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                    <Package weight="duotone" className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="overline">Container</p>
                    <p className="font-mono font-bold">{order.container_number || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                    <FileText weight="duotone" className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="overline">Bill of Lading</p>
                    <p className="font-mono font-bold">{order.bl_number || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                    <MapPin weight="duotone" className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="overline">Current Location</p>
                    <p className="font-bold">{order.current_location || 'Unknown'}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                    <Clock weight="duotone" className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="overline">Departure Date</p>
                    <p className="font-bold">{order.departure_date || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                    <Clock weight="duotone" className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="overline">Estimated Arrival</p>
                    <p className="font-bold">{order.estimated_arrival || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                    <Truck weight="duotone" className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="overline">Tracking Reference</p>
                    <p className="font-mono">{order.tracking_reference || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          {/* Sharable client upload link */}
          <div className="card-brutalist mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold flex items-center gap-2">
                <LinkSimple weight="duotone" /> Client upload link
              </h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Generate a secure link the client can use (without logging in) to upload documents directly to this order. You can also email the link or attach a note.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <div>
                <Label className="text-xs">Expires in (days)</Label>
                <Input
                  type="number" min={1} max={90}
                  value={tokenForm.expires_in_days}
                  onChange={(e) => setTokenForm({ ...tokenForm, expires_in_days: e.target.value })}
                  data-testid="token-expires-input"
                />
              </div>
              <div>
                <Label className="text-xs">Max uploads</Label>
                <Input
                  type="number" min={1} max={100}
                  value={tokenForm.max_uploads}
                  onChange={(e) => setTokenForm({ ...tokenForm, max_uploads: e.target.value })}
                  data-testid="token-max-uploads-input"
                />
              </div>
              <div className="md:col-span-2 flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={tokenForm.send_email}
                    onChange={(e) => setTokenForm({ ...tokenForm, send_email: e.target.checked })}
                    data-testid="token-send-email-checkbox"
                  />
                  Email this link to the client
                </label>
              </div>
            </div>
            {tokenForm.send_email && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <Label className="text-xs">Send to email (optional)</Label>
                  <Input
                    type="email"
                    placeholder="Leave empty to use the client's email on file"
                    value={tokenForm.send_to_email}
                    onChange={(e) => setTokenForm({ ...tokenForm, send_to_email: e.target.value })}
                    data-testid="token-email-input"
                  />
                </div>
              </div>
            )}
            <div className="mb-3">
              <Label className="text-xs">Note to client (optional)</Label>
              <Textarea
                rows={2}
                placeholder="e.g. Please upload your passport scan and proof of address"
                value={tokenForm.note_to_client}
                onChange={(e) => setTokenForm({ ...tokenForm, note_to_client: e.target.value })}
                data-testid="token-note-input"
              />
            </div>
            <Button
              onClick={handleCreateToken}
              disabled={creatingToken}
              className="btn-primary"
              data-testid="create-upload-token-btn"
            >
              <LinkSimple className="w-4 h-4 mr-2" />
              {creatingToken ? 'Creating…' : 'Create upload link'}
            </Button>

            {tokens.length > 0 && (
              <div className="mt-5 space-y-2">
                <p className="overline text-[10px] uppercase text-muted-foreground">Active & past links</p>
                {tokens.map((tok) => {
                  const st = tokenStatus(tok);
                  const url = `${window.location.origin}/u/${tok.token}`;
                  return (
                    <div key={tok.id} className="border border-border rounded p-3" data-testid={`token-row-${tok.id}`}>
                      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${st.color}`}>{st.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {tok.uploads_count || 0} / {tok.max_uploads} uploads · expires {new Date(tok.expires_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => copyTokenUrl(tok)} data-testid={`copy-token-${tok.id}`}>
                            <Copy className="w-3 h-3 mr-1" /> Copy
                          </Button>
                          {!tok.is_revoked && (
                            <Button size="sm" variant="ghost" onClick={() => handleRevokeToken(tok)} className="text-destructive" data-testid={`revoke-token-${tok.id}`}>
                              <Prohibit className="w-3 h-3 mr-1" /> Revoke
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-xs font-mono break-all text-muted-foreground">{url}</p>
                      {tok.note_to_client && (
                        <p className="text-xs italic text-muted-foreground mt-1">"{tok.note_to_client}"</p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Created {new Date(tok.created_at).toLocaleString()}
                        {tok.created_by_name ? ` · by ${tok.created_by_name}` : ''}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="card-brutalist">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold">Order documents</h3>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  multiple
                  accept="image/*,application/pdf"
                  data-testid="file-upload-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="btn-primary"
                  data-testid="upload-document-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : t('common.upload')}
                </Button>
              </div>
            </div>

            {documents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No documents uploaded yet
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => {
                  const fileUrl = getFileUrl(doc.id);
                  const isImage = (doc.content_type || '').startsWith('image/');
                  const isPdf = (doc.content_type || '') === 'application/pdf';
                  const canPreview = isImage || isPdf;
                  return (
                    <div key={doc.id} className="flex items-center justify-between p-3 border border-border hover:bg-muted/30 transition-colors">
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 min-w-0 flex-1 group"
                        data-testid={`doc-open-${doc.id}`}
                        title={canPreview ? 'Open in new tab' : 'Download'}
                      >
                        <FileText weight="duotone" className="w-5 h-5 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate group-hover:text-primary group-hover:underline">
                            {doc.original_filename}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {((doc.size || 0) / 1024).toFixed(1)} KB · {new Date(doc.created_at).toLocaleString()}
                            {doc.uploaded_source === 'client_link'
                              ? <span className="ml-2 text-[10px] bg-green-50 text-green-700 px-1 py-0.5 rounded">via client link</span>
                              : doc.uploaded_by_name
                                ? <span className="ml-2 text-[10px] text-muted-foreground">by {doc.uploaded_by_name}</span>
                                : null}
                          </p>
                        </div>
                      </a>
                      <div className="flex items-center gap-1 ml-2 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                          title={canPreview ? 'View' : 'Open'}
                          data-testid={`doc-view-${doc.id}`}
                        >
                          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                            <Eye className="w-4 h-4" />
                          </a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                          title="Download"
                          data-testid={`doc-download-${doc.id}`}
                        >
                          <a href={fileUrl} download={doc.original_filename}>
                            <Download className="w-4 h-4" />
                          </a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteFile(doc.id)}
                          className="text-destructive"
                          title="Delete"
                          data-testid={`doc-delete-${doc.id}`}
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <div className="card-brutalist">
            <h3 className="font-bold mb-4">{t('orders.statusHistory')}</h3>
            {order.status_history?.length > 0 ? (
              <div className="space-y-4">
                {order.status_history.map((entry, idx) => (
                  <div key={`status-${entry.status}-${entry.timestamp || idx}`} className="flex items-start gap-4">
                    <div className={`w-8 h-8 flex items-center justify-center ${
                      entry.status === 'delivered' ? 'bg-green-100' : 
                      entry.status === 'cancelled' ? 'bg-red-100' : 'bg-secondary'
                    }`}>
                      {entry.status === 'delivered' ? (
                        <CheckCircle weight="fill" className="w-4 h-4 text-green-600" />
                      ) : entry.status === 'cancelled' ? (
                        <Warning weight="fill" className="w-4 h-4 text-red-600" />
                      ) : (
                        <Clock weight="duotone" className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`status-badge status-${entry.status}`}>
                          {statusLabels[entry.status]}
                        </span>
                        {entry.previous_status && (
                          <span className="text-xs text-muted-foreground">
                            from {statusLabels[entry.previous_status]}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(entry.timestamp).toLocaleString()}
                      </p>
                      {entry.notes && (
                        <p className="text-sm text-muted-foreground mt-1">{entry.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No status history available
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrderDetailPage;
